import { useApiUrl, useGo } from "@refinedev/core";
import { useEffect, useState } from "react";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Compass,
  FolderKanban,
  ListTodo,
  Sparkles,
} from "@/components/ui/icon";

import {
  EVIDENCE_POLARITY,
  PACKAGE_MATURITY_STAGES,
  SIGNAL_STATUS,
  type DashboardOverview,
  type EvidencePolarity,
  type PackageMaturityStage,
  type SignalStatus,
} from "@aos/shared";
import { PageHeader } from "@/components/layout/PageHeader";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { MetricCard, StatusBadge } from "@/components/app";
import { EmptyState, ErrorState } from "@/components/ui/states";
import { RefLink } from "@/components/inspector";
import { humanize } from "@/lib/format";

/**
 * AOS v0.2 advisory metrics (D3 — context coverage is a *metric*, never a gate).
 *
 * `ContextCoverage` is the shape of `GET /api/dashboard/context-coverage`
 * (contract §3.4, owned by B5). The package/claim/evidence breakdowns arrive as
 * additive optional fields on `DashboardOverview` (contract: B5 extends the DTO
 * "only additively"). Both are consumed defensively here so the cards degrade to
 * "hidden / —" while the backend halves of the build land.
 */
interface ContextCoverage {
  artifacts_total: number;
  artifacts_with_context: number;
  coverage_ratio: number; // 0..1
  contexts_by_maturity: Record<string, number>;
  signals_by_status: Partial<Record<SignalStatus, number>>;
}

/** Additive v0.2 fields B5 layers onto the v0.1 `DashboardOverview` payload. */
interface DashboardOverviewV2 {
  packages_by_maturity?: Partial<Record<PackageMaturityStage, number>>;
  open_claims?: number;
  evidence_by_polarity?: Partial<Record<EvidencePolarity, number>>;
}

/** Fetch JSON from the worker API, sending the session cookie. */
async function fetchJson<T>(url: string, signal: AbortSignal): Promise<T> {
  const res = await fetch(url, { credentials: "include", signal });
  if (!res.ok) {
    throw new Error(`Request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

/** Like `fetchJson` but resolves `null` on any failure (advisory metrics). */
async function fetchJsonOptional<T>(url: string, signal: AbortSignal): Promise<T | null> {
  try {
    return await fetchJson<T>(url, signal);
  } catch {
    return null;
  }
}

/** A Refine-style list envelope: `{ items, total }`. */
interface ListEnvelope<T> {
  items: T[];
  total: number;
}

/**
 * The dashboard's v0.2 package/claim/evidence breakdowns, aggregated client-side.
 *
 * B5's additive `DashboardOverviewV2` fields aren't populated on staging yet, so
 * rather than render "—" over data that plainly exists we fold the list
 * endpoints here. The shapes mirror the DTO fields exactly, so the wiring below
 * prefers the server aggregate the moment it appears and falls back to this.
 */
interface AggregatedMetrics {
  packages_by_maturity: Record<string, number>;
  evidence_by_polarity: Record<string, number>;
  open_claims: number;
}

/** Count list rows by a string field. */
function tallyBy(
  env: ListEnvelope<Record<string, unknown>> | null,
  field: string,
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const row of env?.items ?? []) {
    const key = row[field];
    if (typeof key === "string") counts[key] = (counts[key] ?? 0) + 1;
  }
  return counts;
}

/**
 * Aggregate the v0.2 breakdowns from the list endpoints (advisory, never fatal).
 * Resolves `null` only if every fetch fails, so the card simply stays hidden.
 * `pageSize=500` comfortably covers the staging dataset (58 packages, 12
 * evidence); claims needs just `total`, so a single row is enough.
 */
async function fetchAggregatedMetrics(
  apiUrl: string,
  signal: AbortSignal,
): Promise<AggregatedMetrics | null> {
  const [pkgs, ev, claims] = await Promise.all([
    fetchJsonOptional<ListEnvelope<Record<string, unknown>>>(
      `${apiUrl}/packages?pageSize=500`,
      signal,
    ),
    fetchJsonOptional<ListEnvelope<Record<string, unknown>>>(
      `${apiUrl}/evidence?pageSize=500`,
      signal,
    ),
    fetchJsonOptional<ListEnvelope<unknown>>(`${apiUrl}/claims?pageSize=1`, signal),
  ]);
  if (pkgs == null && ev == null && claims == null) return null;
  return {
    packages_by_maturity: tallyBy(pkgs, "maturity_stage"),
    evidence_by_polarity: tallyBy(ev, "polarity"),
    open_claims: claims?.total ?? 0,
  };
}

/** A labelled count breakdown rendered as a wrapped row of badges. */
function Breakdown({
  counts,
  order,
}: {
  counts: Partial<Record<string, number>>;
  order: readonly string[];
}) {
  // Lead with the canonical order, then append any live keys it doesn't cover
  // (e.g. a `maturity_stage` the shared enum predates) so no real count is lost.
  const keys = [
    ...order,
    ...Object.keys(counts).filter((k) => !order.includes(k)),
  ];
  const entries = keys
    .map((key) => [key, counts[key] ?? 0] as const)
    .filter(([, n]) => n > 0);
  if (entries.length === 0) {
    return <p className="text-sm text-muted-foreground">No data yet.</p>;
  }
  return (
    <div className="flex flex-wrap gap-2">
      {entries.map(([key, n]) => (
        <Badge key={key} variant="secondary" className="font-normal">
          <span className="font-mono text-[10px] uppercase tracking-wide">{key}</span>
          <span className="ml-1.5 font-semibold">{n}</span>
        </Badge>
      ))}
    </div>
  );
}

/**
 * Dashboard (D6) — spec §13.1 module 1. Renders MetricCards, recent events,
 * failed rule runs, and the agent-run status summary from
 * `GET /api/dashboard/overview` (contract §7, DTO `DashboardOverview`).
 */
export function Dashboard() {
  const apiUrl = useApiUrl();
  const go = useGo();
  const [data, setData] = useState<DashboardOverview | null>(null);
  const [coverage, setCoverage] = useState<ContextCoverage | null>(null);
  const [aggregated, setAggregated] = useState<AggregatedMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    // The v0.2 coverage metric is advisory (D3): fetched alongside the overview
    // but never allowed to fail the page — it resolves to null when the B5
    // endpoint is absent.
    Promise.all([
      fetchJson<DashboardOverview>(`${apiUrl}/dashboard/overview`, controller.signal),
      fetchJsonOptional<ContextCoverage>(
        `${apiUrl}/dashboard/context-coverage`,
        controller.signal,
      ),
      fetchAggregatedMetrics(apiUrl, controller.signal),
    ])
      .then(([overview, cov, agg]) => {
        setData(overview);
        setCoverage(cov);
        setAggregated(agg);
      })
      .catch((e: unknown) => {
        if (controller.signal.aborted) return;
        setError(e instanceof Error ? e.message : "Failed to load dashboard");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [apiUrl]);

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Dashboard"
          subtitle="A live snapshot of projects, tasks, agents, and context coverage."
          icon="dashboard"
        />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Dashboard"
          subtitle="A live snapshot of projects, tasks, agents, and context coverage."
          icon="dashboard"
        />
        <ErrorState message={error ?? "No data returned."} />
      </div>
    );
  }

  const summary = data.agent_runs_summary;
  const v2 = data as DashboardOverview & DashboardOverviewV2;
  const coveragePct =
    coverage != null ? Math.round(coverage.coverage_ratio * 100) : null;
  // Prefer B5's server aggregate the moment it lands; until then fall back to the
  // client-side tally of the list endpoints (the staging data exists today).
  const packagesByMaturity = v2.packages_by_maturity ?? aggregated?.packages_by_maturity ?? null;
  const evidenceByPolarity = v2.evidence_by_polarity ?? aggregated?.evidence_by_polarity ?? null;
  const openClaims = v2.open_claims ?? aggregated?.open_claims ?? null;
  // The advisory v0.2 block renders only when at least one metric source is live.
  const hasV2Metrics =
    coverage != null ||
    packagesByMaturity != null ||
    evidenceByPolarity != null ||
    openClaims != null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        subtitle="A live snapshot of projects, tasks, agents, and context coverage."
        icon="dashboard"
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <MetricCard title="Projects" value={data.counts.projects} icon={FolderKanban} />
        <MetricCard title="Tasks" value={data.counts.tasks} icon={ListTodo} />
        <MetricCard title="Open tasks" value={data.counts.open_tasks} icon={Activity} />
        <MetricCard title="Skills published" value={data.counts.skills_published} icon={Sparkles} />
        <MetricCard title="Agent runs today" value={data.counts.agent_runs_today} icon={Activity} />
      </div>

      {hasV2Metrics && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Compass className="size-4 text-muted-foreground" /> Context coverage
              <Badge variant="outline" className="ml-1 font-normal">
                advisory
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-md border border-border p-4">
                <div className="text-sm text-muted-foreground">Coverage</div>
                <div className="text-2xl font-bold">
                  {coveragePct != null ? `${coveragePct}%` : "—"}
                </div>
                {coverage != null && (
                  <div className="mt-1 text-xs text-muted-foreground">
                    {coverage.artifacts_with_context} / {coverage.artifacts_total} artifacts
                  </div>
                )}
              </div>
              <div className="rounded-md border border-border p-4">
                <div className="text-sm text-muted-foreground">Open claims</div>
                <div className="text-2xl font-bold">
                  {openClaims ?? "—"}
                </div>
                {openClaims === 0 && (
                  <div className="mt-1 text-xs text-muted-foreground">None yet</div>
                )}
              </div>
              <div className="rounded-md border border-border p-4 sm:col-span-2">
                <div className="mb-2 text-sm text-muted-foreground">Signals by status</div>
                {coverage != null ? (
                  <Breakdown counts={coverage.signals_by_status} order={SIGNAL_STATUS} />
                ) : (
                  <p className="text-sm text-muted-foreground">—</p>
                )}
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <div className="rounded-md border border-border p-4">
                <div className="mb-2 text-sm text-muted-foreground">Packages by maturity</div>
                {packagesByMaturity != null ? (
                  <Breakdown
                    counts={packagesByMaturity}
                    order={PACKAGE_MATURITY_STAGES}
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">—</p>
                )}
              </div>
              <div className="rounded-md border border-border p-4">
                <div className="mb-2 text-sm text-muted-foreground">Evidence by polarity</div>
                {evidenceByPolarity != null ? (
                  <Breakdown counts={evidenceByPolarity} order={EVIDENCE_POLARITY} />
                ) : (
                  <p className="text-sm text-muted-foreground">—</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Agent runs</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className="rounded-md border border-border p-4">
              <div className="text-sm text-muted-foreground">Queued</div>
              <div className="text-xl font-semibold">{summary.queued}</div>
            </div>
            <div className="rounded-md border border-border p-4">
              <div className="text-sm text-muted-foreground">Running</div>
              <div className="text-xl font-semibold">{summary.running}</div>
            </div>
            <div className="rounded-md border border-border p-4">
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <CheckCircle2 className="size-3.5" /> Completed
              </div>
              <div className="text-xl font-semibold">{summary.completed}</div>
            </div>
            <div className="rounded-md border border-border p-4">
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <AlertTriangle className="size-3.5" /> Failed
              </div>
              <div className="text-xl font-semibold">{summary.failed}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Recent events</CardTitle>
          </CardHeader>
          <CardContent>
            {data.recent_events.length === 0 ? (
              <EmptyState
                icon="events"
                title="No recent events"
                hint="Activity will appear here as the system processes work."
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>When</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.recent_events.map((ev) => (
                    <TableRow
                      key={ev.id}
                      className="cursor-pointer"
                      onClick={() => go({ to: { resource: "events", action: "show", id: ev.id } })}
                    >
                      <TableCell className="font-medium">{humanize(ev.event_type)}</TableCell>
                      <TableCell>
                        <StatusBadge status={ev.status} />
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(ev.created_at).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="size-4 text-destructive" /> Failed rule runs
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.failed_rule_runs.length === 0 ? (
              <EmptyState
                icon="resultOk"
                title="No failed rule runs"
                hint="All rule evaluations are passing."
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Rule</TableHead>
                    <TableHead>Error</TableHead>
                    <TableHead>When</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.failed_rule_runs.map((rr) => (
                    <TableRow
                      key={rr.id}
                      className="cursor-pointer"
                      onClick={() => go({ to: { resource: "rule-runs", action: "show", id: rr.id } })}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <RefLink resource="rules" id={rr.rule_id} />
                      </TableCell>
                      <TableCell className="max-w-[16rem] truncate text-destructive">
                        {rr.error ?? "—"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(rr.started_at).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default Dashboard;
